import { sentenceCase } from 'change-case';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
  Grid,
  CardHeader,
  CardContent,
  Paper,
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
// components
import Label from '../../../components/label';
import Scrollbar from '../../../components/scrollbar';
// sections
import { UserListHead } from '../../../sections/@dashboard/user';

import { Page } from '../../../components';
import { getDisposals } from '../store/disposalActions';
import { fDateTime } from '../../../utils/formatTime';
import { getProfile } from '../../../shared/auth/authService';
import { Accept, Decline, DisposalFilter, EMPTY_DISPOSAL_FILTERS, NewDisposal } from '../components';
import useUrlFilters from '../../../hooks/useUrlFilters';
import Iconify from '../../../components/iconify';
import { fCurrency } from '../../../utils/formatNumber';
import { useHasRole } from '../../../shared/auth/useProfile';
import { ROLES } from '../../../shared/auth/roles';
import { requestKey } from '../../workflow/store/workflowConstants';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'date', label: 'Date', alignRight: false },
  { id: 'asset', label: 'Asset', alignRight: false },
  { id: 'barcodeNumber', label: 'Barcode Number', alignRight: false },
  { id: 'branch', label: 'Branch', alignRight: false },
  { id: 'requestedBy', label: 'Requested By', alignRight: false },
  { id: 'customerName', label: 'Customer Name', alignRight: false },
  { id: 'customerPhone', label: 'Customer Phone', alignRight: false },
  { id: 'estimateAmount', label: 'Estimate Amount', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];
// ----------------------------------------------------------------------

export default function Disposals() {
  // Role flags are derived per mount, not once at module load.
  const isManager = useHasRole(ROLES.MANAGER);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, disposals, pagination } = useSelector((state) => state.disposalReducer);
  //

  // The filters and the page live in the URL, so a refresh — or Back from a
  // disposal — comes back to the same rows rather than to page 1 of everything.
  const { filters, hasFilters, page, rowsPerPage, setPage, setRowsPerPage, applyFilters, clearFilters } =
    useUrlFilters(EMPTY_DISPOSAL_FILTERS);

  const [user, setUser] = useState({});
  const [open, setOpen] = useState(false);
  const [openDisposal, setOpenDisposal] = useState(false);
  const [openAccept, setOpenAccept] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  // What is being typed, as opposed to what is being searched on: the URL
  // changes when the search is run, not on every keystroke.
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm);

  // Follow the URL when it changes underneath us — the Back button, or a link
  // someone pasted with a search already in it.
  useEffect(() => {
    setSearchTerm(filters.searchTerm);
  }, [filters]);

  // Committing the box to the URL is the whole of it: the effect below fetches
  // off the filters, so there is no second dispatch here.
  const runSearch = () => {
    if (searchTerm === filters.searchTerm) return;
    applyFilters({ ...filters, searchTerm });
  };

  const refresh = () => dispatch(getDisposals({ page: page + 1, rowsPerPage, ...filters }));

  const handleClose = () => {
    setOpen(!open);
    setTimeout(refresh, 2000); // 2000 mil
  };
  const handleOpenDisposal = () => {
    setOpenDisposal(!openDisposal);
    setTimeout(refresh, 2000); // 2000 mil
  };

  const handleCloseAccept = () => {
    setOpenAccept(!openAccept);
    setTimeout(refresh, 2000); // 2000 mil
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  useEffect(() => {
    dispatch(getDisposals({ page: page + 1, rowsPerPage, ...filters }));
  }, [dispatch, page, rowsPerPage, filters]);

  useEffect(() => {
    const profile = getProfile();
    if (profile) {
      setUser(profile);
    }
  }, []);

  return (
    <>
      <Page title="Disposals">
        {openDisposal && <NewDisposal open={openDisposal} handleClose={handleOpenDisposal} />}
        {openAccept && <Accept open={openAccept} handleClose={handleCloseAccept} disposalId={selectedRow?.id} />}
        {open && <Decline open={open} handleClose={handleClose} disposalId={selectedRow?.id} />}
        {openFilter && (
          <DisposalFilter
            open={openFilter}
            handleClose={() => setOpenFilter(false)}
            values={filters}
            onApply={applyFilters}
          />
        )}
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Card>
                {/* {isManager && (
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" sx={{ float: 'right' }} spacing={0.5}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setOpenHandover(true)}
                        startIcon={<Iconify icon="eva:plus-fill" />}
                      >
                        New Handover
                      </Button>
                    </Stack>
                  </Box>
                )} */}
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" sx={{ float: 'right' }} spacing={1}>
                    <TextField
                      size="small"
                      sx={{ minWidth: { xs: 180, sm: 280 } }}
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      onBlur={runSearch}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') runSearch();
                      }}
                      placeholder="Search asset, barcode or customer..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => setOpenFilter(true)}
                      startIcon={<Iconify icon="eva:funnel-fill" />}
                    >
                      {hasFilters ? 'Filters applied' : 'Filter Disposals'}
                    </Button>
                    {hasFilters && (
                      <Button size="small" variant="outlined" onClick={clearFilters}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                </Box>
                <CardHeader title={'Disposals'} subheader={''} />
                <CardContent>
                  <Scrollbar>
                    <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                      {isLoading ? (
                        <Grid container sx={{ justifyContent: 'center' }}>
                          <ThreeDots
                            height="80"
                            width="80"
                            radius="9"
                            color="#2065d1"
                            ariaLabel="three-dots-loading"
                            wrapperStyle={{}}
                            wrapperClassName=""
                            visible
                          />
                        </Grid>
                      ) : (
                        <Table size="small">
                          <UserListHead headLabel={TABLE_HEAD} rowCount={disposals?.length} />
                          <TableBody>
                            {disposals?.length > 0 ? (
                              disposals.map((record) => (
                                <TableRow hover key={record?.id} tabIndex={-1} role="checkbox">
                                  <TableCell align="left">{fDateTime(record?.date_created)}</TableCell>
                                  <TableCell align="left">{record?.asset?.name}</TableCell>
                                  <TableCell align="left">{record?.asset?.barcode_number}</TableCell>
                                  <TableCell align="left">{record?.branch?.name}</TableCell>
                                  <TableCell align="left">{record?.staff?.user?.display_name}</TableCell>
                                  <TableCell align="left">{record?.customer_name}</TableCell>
                                  <TableCell align="left">{record?.mobile_number}</TableCell>
                                  <TableCell align="left">{fCurrency(record?.estimate_amount)}</TableCell>

                                  <TableCell align="left">
                                    <Label
                                      color={
                                        (record?.status === 'PENDING' && 'warning') ||
                                        (record?.status === 'REJECTED' && 'error') ||
                                        'success'
                                      }
                                    >
                                      {sentenceCase(record?.status)}
                                    </Label>
                                  </TableCell>
                                  <TableCell>
                                    <Stack spacing={2} direction="row">
                                      {user?.role === 'ADMIN' && record?.status === 'PENDING' && (
                                        <>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                              setSelectedRow(record);
                                              setOpenAccept(true);
                                            }}
                                          >
                                            Approve
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => {
                                              setSelectedRow(record);
                                              setOpen(true);
                                            }}
                                          >
                                            Reject
                                          </Button>
                                        </>
                                      )}

                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          navigate(`/dashboard/requests/${requestKey('disposal', record?.id)}`)
                                        }
                                      >
                                        History
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <>Disposal requests will appear here</>
                            )}
                          </TableBody>
                        </Table>
                      )}
                    </TableContainer>
                  </Scrollbar>

                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={pagination?.count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
